// Button — shadcn-style primitive with CVA-backed variants.
//
// Variants:
//   primary      — brand-filled (default CTA)
//   secondary    — muted surface
//   ghost        — transparent, hover-tinted
//   destructive  — danger action (color IMMUTABLE per spec §7.2)
//   outline      — 1px border, transparent background
//   link         — inline, underline on hover
// Sizes: sm | md | lg | icon.
// Focus ring is bound to `--color-ring` (IMMUTABLE) for a11y consistency.
//
// `asChild` (Radix Slot) lets consumers render into <a> / Next <Link> while
// keeping the button styling.

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from './utils/cn.js';

export const buttonVariants = cva(
  // base
  [
    'inline-flex items-center justify-center gap-2',
    'whitespace-nowrap rounded-md text-sm font-medium',
    'transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
        secondary: 'bg-muted text-foreground hover:bg-muted/80',
        ghost: 'text-foreground hover:bg-muted',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-border bg-transparent text-foreground hover:bg-muted',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4 py-2',
        lg: 'h-11 px-6 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, type, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      // Native <button> defaults to type="submit" inside forms; we default
      // to "button" to avoid accidental form submissions. Caller can
      // override with type="submit" explicitly.
      type={asChild ? type : (type ?? 'button')}
      {...props}
    />
  );
});
